import { Controller } from "@hotwired/stimulus";
import { computePosition, flip, shift, offset } from "@floating-ui/dom";
import { useWindowResize } from "stimulus-use"

export default class extends Controller {
  static targets = ["trigger", "menu", "menuItem"];
  static values = {
    open: {
      type: Boolean,
      default: false,
    },
    options: {
      type: Object,
      default: {},
    },
  }

  connect() {
    //console.log("Dropdown controller connected", this.optionsValue ); 
    this.boundHandleKeydown = this.#handleKeydown.bind(this); // Bind the function so we can remove it later
    this.selectedIndex = -1;
    useWindowResize(this)
  }

  windowResize({ width, height, event }) {
    if(!this.hasMenuTarget || !this.hasTriggerTarget) return
    this.#computeTooltip()
  }

  
  #computeTooltip() {
    const middleware = [shift(), offset(8)];
    if (this.optionsValue.flip !== false) middleware.unshift(flip());
    computePosition(this.triggerTarget, this.menuTarget, {
      placement: this.optionsValue.placement || "bottom",
      middleware,
    }).then(({ x, y }) => {
      Object.assign(this.menuTarget.style, {
        left: `${x}px`,
        top: `${y}px`,
      });
    });
  }

  get innerMenuTarget() {
    return this.menuTarget.querySelector('div');
  }

  #computeWidth() {
    if(this.innerMenuTarget.offsetWidth == this.triggerTarget.offsetWidth) return;
    if (this.innerMenuTarget.offsetWidth < this.triggerTarget.offsetWidth) {
      this.innerMenuTarget.style.width = `${this.triggerTarget.offsetWidth}px`;
    } else {
      this.innerMenuTarget.style.width = "";
    }
  }

  onClickOutside(event) {
    if (!this.openValue) return;
    if (this.element.contains(event.target)) return;

    event.preventDefault();
    this.close();
  }

  toggle() { 
    //console.debug("Toggle dropdown");
    this.menuTarget.classList.contains("hidden") ? this.open() : this.close();
  }

  open() {
    this.openValue = true;
    this.#deselectAll();
    this.#addEventListeners();
    this.#computeTooltip()
    this.menuTarget.classList.remove("hidden");
    this.#computeWidth()
    this.dispatch('show')
  }

  close() {
    this.openValue = false;
    this.#removeEventListeners();
    this.menuTarget.classList.add("hidden");
    this.dispatch('hide')
  }

  #handleKeydown(e) {
    // return if no menu items (one line fix for)
    if (this.menuItemTargets.length === 0) { return; }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      this.#updateSelectedItem(1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      this.#updateSelectedItem(-1);
    } else if (e.key === 'Enter' && this.selectedIndex !== -1) {
      e.preventDefault();
      this.menuItemTargets[this.selectedIndex].click();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      this.close();
    }
  }

  #updateSelectedItem(direction) {
    // Check if any of the menuItemTargets have aria-selected="true" and set the selectedIndex to that index
    this.menuItemTargets.forEach((item, index) => {
      if (item.getAttribute('aria-selected') === 'true') {
        this.selectedIndex = index;
      }
    });

    if (this.selectedIndex >= 0) {
      this.toggleAriaSelected(this.menuItemTargets[this.selectedIndex], false);
    }

    this.selectedIndex += direction;

    if (this.selectedIndex < 0) {
      this.selectedIndex = this.menuItemTargets.length - 1;
    } else if (this.selectedIndex >= this.menuItemTargets.length) {
      this.selectedIndex = 0;
    }

    this.toggleAriaSelected(this.menuItemTargets[this.selectedIndex], true);
  }

  get firstItem() {
    return this.menuItemTargets[0]
  }

  toggleAriaSelected(element, isSelected) {
    // Add or remove attribute
    if (isSelected) {
      element.setAttribute('aria-selected', 'true');
    } else {
      element.removeAttribute('aria-selected');
    }
  }

  #deselectAll() {
    this.menuItemTargets.forEach(item => this.toggleAriaSelected(item, false));
    this.selectedIndex = -1;
  }

  #addEventListeners() {
    document.addEventListener('keydown', this.boundHandleKeydown);
  }

  #removeEventListeners() {
    document.removeEventListener('keydown', this.boundHandleKeydown);
  }
}  